import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ESSAP_CATS } from "@/lib/essap";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/site/ImageUpload";
import { PdfUpload } from "@/components/admin/PdfUpload";

const EMPTY = { titre: "", contenu: "", categorie: "communiques", image: null, fichier: null, fichier_nom: null, publie: true };

export const EssapPostDialog = ({ open, onOpenChange, post, defaultCat, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(post ? { ...EMPTY, ...post } : { ...EMPTY, categorie: defaultCat || "communiques" });
  }, [open, post, defaultCat]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { titre: f.titre, contenu: f.contenu, categorie: f.categorie, image: f.image, fichier: f.fichier, fichier_nom: f.fichier_nom, publie: f.publie };
    setBusy(true);
    try {
      if (post) await api.put(`/admin/essap/posts/${post.id}`, payload);
      else await api.post("/admin/essap/posts", payload);
      toast.success(post ? "Publication mise à jour" : "Publication enregistrée");
      onSaved();
      onOpenChange(false);
    } catch (ex) { toast.error(errMsg(ex)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="essap-post-dialog" aria-describedby={undefined} className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{post ? "Modifier la publication" : "Nouvelle publication ESSAP"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1.5"><Label>Rubrique *</Label>
            <Select value={f.categorie} onValueChange={(v) => setF({ ...f, categorie: v })}>
              <SelectTrigger data-testid="essap-post-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ESSAP_CATS.map((c) => <SelectItem key={c.key} value={c.key} data-testid={`essap-cat-option-${c.key}`}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Titre *</Label><Input required data-testid="essap-post-title-input" value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Texte</Label><Textarea rows={8} data-testid="essap-post-content-input" value={f.contenu} onChange={(e) => setF({ ...f, contenu: e.target.value })} placeholder="Vous pouvez coller ici une liste (un nom par ligne)…" /></div>
          <div className="space-y-1.5"><Label>Document PDF (optionnel)</Label><PdfUpload value={f.fichier} name={f.fichier_nom} onChange={(p, n) => setF({ ...f, fichier: p, fichier_nom: n })} testId="essap-post-pdf-upload" /></div>
          <div className="space-y-1.5"><Label>Image (optionnelle)</Label><ImageUpload value={f.image} onChange={(v) => setF({ ...f, image: v })} testId="essap-post-image-upload" /></div>
          <div className="flex items-center gap-3">
            <Switch id="essap-publie" checked={f.publie} onCheckedChange={(v) => setF({ ...f, publie: v })} data-testid="essap-post-publish-switch" />
            <Label htmlFor="essap-publie">Visible publiquement</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="essap-post-cancel">Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="essap-post-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
